'use client';

import React, { useEffect, useState } from 'react';
import { Breadcrumbs } from '@/components/core';
import { ProductType, RecommendationType } from '@/@types';
import { useLingui } from '@lingui/react';
import { useUser } from '@/components/context/user';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { notFound } from 'next/navigation';
import configuration from '@/configuration';
import { useLocalizedLinks } from '@/components/core/hooks';
import { ScreenContainer } from '@/components/core/components/atoms';
import { categories } from '@/@constants';
import * as styles from './product.styles';
import Actions from './components/actions';
import Details from './components/details';
import Comments from './components/comments';
import Recommendations from './components/recommendations';
import MoreLikeThis from './components/more-like-this';
import ProductRatings from './components/ratings';
import ProductHeader from './components/header';
import ProductAside from './components/aside';

const ProductPage: React.FC<{ product: ProductType; referer?: RecommendationType }> = ({ product: propProduct, referer }) => {
  const { emitter } = useUser();
  const [product, setProduct] = useState<ProductType>(propProduct);
  const links = useLocalizedLinks();
  const { i18n } = useLingui();
  const category = categories[product.category.name];

  useEffect(() => {
    setProduct(propProduct);
  }, [propProduct]);

  useEffect(() => {
    if (!product) {
      return undefined;
    }
    const listener = ({ detail }) => {
      if (product?.id === detail.productId) {
        setProduct({
          ...product,
          isSaved: detail.isSaved,
        });
      }
    };
    emitter?.addEventListener('toggle-product-collection', listener);
    return () => {
      emitter?.removeEventListener('toggle-product-collection', listener);
    };
  }, [emitter, product]);

  if (!product) {
    return notFound();
  }

  try {
    return (
      <ScreenContainer className={styles.root}>
        <Breadcrumbs className={styles.breadcrumbs} host={configuration.host}>
          <Breadcrumbs.Item href={links.explore()} name={i18n._(msg`Explore`)}>
            <Trans>Explore</Trans>
          </Breadcrumbs.Item>
          <Breadcrumbs.Item href={links.explore(category)}>{i18n._(category.title)}</Breadcrumbs.Item>
          <Breadcrumbs.Item>{product.name}</Breadcrumbs.Item>
        </Breadcrumbs>
        <ProductHeader product={product} className={styles.header} />
        <ProductAside recommendation={referer} product={product} className={styles.side} />
        <section className={styles.main}>
          <Recommendations product={product} />
          <ProductRatings product={product} />
          <Actions product={product} />
          <Details product={product} />
          <Comments product={product} />
        </section>
        <MoreLikeThis product={product} className={styles.footer} />
      </ScreenContainer>
    );
  } catch (e) {
    return <div>{(e as Error).message}</div>;
  }
};

export default ProductPage;
